import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { udApi } from '@udemy/ud-api';
const GOOGLE_PATH_CERTIFICATE_TYPE = 'google_path';
const CERTIFICATE_URL_FIELDS = ['long_url'];
const GWG_CERTIFICATE_QUERY_KEY = 'gwg-certificate-url';

/**
 * Minimal response item for the `/users/me/certificates/` endpoint.
 * This covers the fields we need to resolve the certificate URL.
 */

/**
 * Response shape for the `/users/me/certificates/` endpoint.
 */

/**
 * Parameters for fetching the accelerator certificate URL.
 */

/**
 * Result of `useGwgCertificateUrl`.
 */

/**
 * Extracts the best certificate URL from a response item.
 * We only return values that are non-empty strings.
 */
function getCertificateUrlFromItem(item) {
  for (const key of CERTIFICATE_URL_FIELDS) {
    const value = item[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

/**
 * Selects the certificate URL for a specific accelerator by matching `path_id`.
 *
 * Strict matching is intentional: we only return a URL when the certificate
 * explicitly belongs to this accelerator. Showing the wrong certificate (e.g.
 * the first one in a list when a user has completed multiple paths) is worse
 * than showing none, so we return `undefined` if no exact match is found.
 *
 * @param items - All certificate records returned from the API.
 * @param acceleratorNumericId - The numeric ID of the accelerator we're rendering.
 */
function selectGooglePathCertificateUrl(items, acceleratorNumericId) {
  const pathCertificateForAccelerator = items.filter(item => item.type === GOOGLE_PATH_CERTIFICATE_TYPE && item.is_ready === true).find(item => item.path_id === String(acceleratorNumericId));
  return pathCertificateForAccelerator ? getCertificateUrlFromItem(pathCertificateForAccelerator) : undefined;
}

/**
 * Fetches certificate records for the current user.
 * This isolates the network request so the hook stays focused on React Query state.
 */
async function fetchGwgCertificateItems(params) {
  var _response$data$result, _response$data;
  const response = await udApi.get('/users/me/certificates/', {
    params
  });
  return (_response$data$result = (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.results) !== null && _response$data$result !== void 0 ? _response$data$result : [];
}

/**
 * Returns true when the course is completed based on enrollment data.
 */
export const isCourseCompleted = course => {
  var _course$userEnrollmen, _course$userEnrollmen2, _course$userEnrollmen3;
  const completionPercentage = (_course$userEnrollmen = (_course$userEnrollmen2 = course.userEnrollment) === null || _course$userEnrollmen2 === void 0 ? void 0 : _course$userEnrollmen2.completionPercentage) !== null && _course$userEnrollmen !== void 0 ? _course$userEnrollmen : 0;
  return completionPercentage >= 100 || Boolean((_course$userEnrollmen3 = course.userEnrollment) === null || _course$userEnrollmen3 === void 0 ? void 0 : _course$userEnrollmen3.completionTime);
};

/**
 * Fetches the certificate URL for a completed Grow with Google path.
 * This mirrors the REST approach used in other repos but targets `type=google_path`.
 */
export function useGwgCertificateUrl({
  accelerator,
  enabled
}) {
  const completedCourseIds = useMemo(() => {
    return accelerator.curriculum.sections.map(section => section.buyable).filter(isCourseCompleted).map(course => course.id);
  }, [accelerator.curriculum.sections]);
  const certificateParams = useMemo(() => {
    const params = {
      type: GOOGLE_PATH_CERTIFICATE_TYPE
    };
    if (completedCourseIds.length > 0) {
      params.course_id = completedCourseIds;
    }
    return params;
  }, [completedCourseIds]);
  const query = useQuery([GWG_CERTIFICATE_QUERY_KEY, accelerator.slug, GOOGLE_PATH_CERTIFICATE_TYPE, completedCourseIds], () => fetchGwgCertificateItems(certificateParams), {
    enabled
  });
  const certificateUrl = useMemo(() => {
    var _query$data;
    return selectGooglePathCertificateUrl((_query$data = query.data) !== null && _query$data !== void 0 ? _query$data : [], accelerator.numericId);
  }, [query.data, accelerator.numericId]);
  return {
    certificateUrl
  };
}
//# sourceMappingURL=use-gwg-certificate-url.js.map